/**
 * `aad-canonical-encoder` — client-side mirror of the recovery-passphrase
 * envelope AAD encoder per Phase B-cryptographic-mechanism AAD spec
 * (`recovery_flow_phase_b_aad_spec_design.md` §3 + DEV1 + DEV5
 * dispositions absorbed at PR #319 + PR #320 v0.1 corrigendum on main).
 *
 * **Mirror source of truth:** `@shn/phg-projection-service`'s
 * `src/recovery/recovery-aad-encoder.ts`
 * ([PR #323](https://github.com/SmartHealthNetwork/SHN2026/pull/323)).
 * This client mirror is byte-for-byte parity with the server encoder
 * by construction: both sides import the same RFC 8785 JCS
 * canonicalizer (`@shn/audit-canonical.canonicalizeForSigning`), define
 * the same 6-field AAD set, enforce the same field-value invariants,
 * and pin the same v1 constants. Drift is structurally limited to
 * field-set or invariant divergence; binding-test taxonomy at
 * `test/recovery/aad-encoder-binding.test.ts` parallels the server
 * binding test at
 * `services/phg-projection-service/test/recovery/aad-canonical-encoding-binding.test.ts`
 * for parallel-failure-on-drift signaling.
 *
 * **Why mirror not import-from-server.** `phg-projection-service` has
 * NodeJS-only AWS SDK transitive deps that would break the browser
 * bundle if imported from the client. Per `feedback_substrate_conventional_split_discipline`:
 * the encoder logic is bounded conventional convenience composing
 * against the real substrate canonical surface; the canonicalizer
 * primitive itself (`@shn/audit-canonical`) is shared verbatim. Mirror
 * approach matches PR #301 `canonical-constants.ts` precedent.
 *
 * **Per `feedback_frost_threshold_property.md`:** shared canonical
 * encoding ≠ shared cryptographic primitive. AEAD-AAD here is distinct
 * from FROST aggregate Schnorr signature surface (ADR-018 §3 mode
 * dispatch axis); only the canonical encoder is shared.
 *
 * **6 required AAD fields v1** (per memo §3.1; DEV1 + DEV5 dispositions
 * cleared at PR #319 + PR #320 trail):
 *   1. `envelopeVersion` — versioning for KDF + AAD shape migration
 *   2. `tenantId` — multi-tenant retrofit binding per ADR-020 §5.5
 *   3. `subjectPciHash` — patient-identity binding
 *   4. `pbkdf2Iterations` — replay protection; mirrors stored value
 *   5. `kdfAlgorithm` — explicit KDF algorithm identifier
 *   6. `recoveryEnvelopeId` — transcript binding for HKDF-combine under
 *      Reading C; mix-and-match resistance per rotation generations
 *
 * **Versioning.** `ENVELOPE_VERSION_V1 = 1`; `kdfAlgorithm: 'pbkdf2-sha256'`;
 * `pbkdf2Iterations: 600_000`. Unknown `envelopeVersion` at unwrap time
 * rejects with `EnvelopeCryptoError 'authentication-failed'` (no partial
 * decode; fail closed). v1.5+ Argon2id migration extends this enum +
 * KDF-parameter shape; sec/comp-lead-on-hire amendment territory per
 * memo §6 Argon2id deferral.
 *
 * **Doctrinal anchors preserved** (memo §5):
 * - NO SHT BACKDOOR: encoder is stateless; no SHT-side decryption
 *   capability introduced; backend stores envelope as opaque ciphertext
 *   + AAD bytes only
 * - Recovery UX honesty: registration-time validation errors are
 *   structural (caller-bug surface); runtime AAD-mismatch surfaces as
 *   generic `EnvelopeCryptoError 'authentication-failed'` per memo §3.4
 * - Recovery ≠ erasure (ADR-024 §2.7.5): structural binding to
 *   `subjectPciHash` + `tenantId` prevents cross-subject envelope reuse
 *   without recovery-completion-for-emergencies pathway
 *
 * **Step 3a scope (canonical-mirror discipline + binding test only).**
 * Substantive `submitPassphrase` wire-up (`deriveCombinedUnwrapKey` +
 * `unwrapKey` + PMK lifecycle via `ActiveSession`) gates on Step 3b
 * design-memo-first cycle activating client-side threshold combine
 * surface (Shamir-or-equivalent k-of-n combine of recovery-element
 * shares per memo §2.1 + PR #156 §3.2). Step 3b activates at Sprint 7
 * SHA v0 onboarding flow portfolio per directive §3 hold-the-line.
 */
import { canonicalizeForSigning } from '@shn/audit-canonical';
/**
 * v1 envelope version. Increment for KDF + AAD shape migrations
 * (e.g., v2 = Argon2id KDF + parameter triple). Unknown versions at
 * unwrap time fail closed.
 */
export const ENVELOPE_VERSION_V1 = 1;
/**
 * v1 KDF algorithm identifier. Paired with `envelopeVersion` for
 * forward-compat. v1.5+ migration to Argon2id changes this identifier
 * + envelope version together.
 */
export const KDF_ALGORITHM_V1 = 'pbkdf2-sha256';
/**
 * Encode the canonical AAD bytes for a recovery-passphrase envelope.
 * Returns RFC 8785 JCS UTF-8 bytes via `@shn/audit-canonical.
 * canonicalizeForSigning`.
 *
 * Validates field-value invariants (non-empty strings; positive
 * integer iterations; v1 version constraint) before canonicalization.
 *
 * @throws Error with message describing the violated invariant if any
 *   field is invalid. v1 AAD spec invariants are surfaced as caller
 *   errors at registration time; v1.5+ rotation-flow callers MUST
 *   produce valid AAD or runtime fails closed.
 */
export function encodeRecoveryAad(fields) {
    if (!Number.isInteger(fields.envelopeVersion) || fields.envelopeVersion < 1) {
        throw new Error('envelopeVersion must be a positive integer');
    }
    if (fields.envelopeVersion !== ENVELOPE_VERSION_V1) {
        // v1.5+ migration territory; fail closed at v1 binding boundary.
        throw new Error(`envelopeVersion ${fields.envelopeVersion} not supported by this binding ` +
            `(v1 only; sec/comp-lead-on-hire amendment for v1.5+ Argon2id)`);
    }
    if (fields.tenantId.length === 0) {
        throw new Error('tenantId must be non-empty');
    }
    if (fields.subjectPciHash.length === 0) {
        throw new Error('subjectPciHash must be non-empty');
    }
    if (!Number.isInteger(fields.pbkdf2Iterations) || fields.pbkdf2Iterations <= 0) {
        throw new Error('pbkdf2Iterations must be a positive integer');
    }
    if (fields.kdfAlgorithm.length === 0) {
        throw new Error('kdfAlgorithm must be non-empty');
    }
    if (fields.kdfAlgorithm !== KDF_ALGORITHM_V1) {
        throw new Error(`kdfAlgorithm '${fields.kdfAlgorithm}' not supported by this binding ` +
            `(v1 only; sec/comp-lead-on-hire amendment for v1.5+ Argon2id)`);
    }
    if (fields.recoveryEnvelopeId.length === 0) {
        throw new Error('recoveryEnvelopeId must be non-empty');
    }
    return canonicalizeForSigning({
        envelopeVersion: fields.envelopeVersion,
        kdfAlgorithm: fields.kdfAlgorithm,
        pbkdf2Iterations: fields.pbkdf2Iterations,
        recoveryEnvelopeId: fields.recoveryEnvelopeId,
        subjectPciHash: fields.subjectPciHash,
        tenantId: fields.tenantId,
    });
}
//# sourceMappingURL=aad-canonical-encoder.js.map